import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Calculator, ChevronRight, Clock, FileText, History, Lock } from 'lucide-react';
import { useAuthStore } from '../store';
import { api, CscaPastPaperCatalogItem, CscaSubjectCatalogItem } from '../utils/api';
const subjectIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  math: Calculator,
};

const CSCAPastPapers: React.FC<{ fixedLevel?: 1 | 2 | 3 }> = ({ fixedLevel }) => {
  const { token } = useAuthStore();
  const [subjects, setSubjects] = useState<CscaSubjectCatalogItem[]>([]);
  const [papers, setPapers] = useState<CscaPastPaperCatalogItem[]>([]);
  const [params, setParams] = useSearchParams();
  const selectedLevel = fixedLevel ?? Number(params.get('level') || 3);
  const setSelectedLevel = (level: number) => setParams({ level: String(level) });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    api.getCscaCatalog(token)
      .then(response => {
        setSubjects(response.subjects || []);
        setPapers(response.papers || []);
        const allowed = (response.subjects || []).filter(subject => subject.available);
        if (fixedLevel && !allowed.some(subject => subject.level === fixedLevel)) {
          setError('当前账号尚未开放这个科目');
          setPapers([]);
        } else if (!fixedLevel && allowed.length && !allowed.some(subject => subject.level === selectedLevel)) {
          setSelectedLevel(allowed[0].level);
        } else {
          setError('');
        }
      })
      .catch((err: Error) => setError(err.message || '无法加载 CSCA 试卷目录'))
      .finally(() => setLoading(false));
  }, [token]);

  const visiblePapers = useMemo(() => papers.filter(paper => paper.level === selectedLevel), [papers, selectedLevel]);
  const topicsPath = fixedLevel ? `/csca/simulation/${fixedLevel}/topics` : `/csca/topic-analysis?level=${selectedLevel}`;
  const accent = selectedLevel === 1
    ? { icon: 'bg-emerald-100 text-emerald-700', text: 'text-emerald-800', button: 'bg-emerald-700 hover:bg-emerald-800' }
    : selectedLevel === 2
      ? { icon: 'bg-orange-100 text-orange-700', text: 'text-orange-800', button: 'bg-orange-700 hover:bg-orange-800' }
      : { icon: 'bg-indigo-100 text-[#4F46E5]', text: 'text-[#4F46E5]', button: 'bg-[#4F46E5] hover:bg-indigo-700' };

  return (
    <div className="mx-auto max-w-6xl space-y-7">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <Link to={fixedLevel && fixedLevel !== 3 ? `/csca/simulation/${fixedLevel}` : '/csca'} className={`inline-flex items-center gap-2 text-sm font-bold ${accent.text}`}>
            <ArrowLeft className="h-4 w-4" /> {fixedLevel && fixedLevel !== 3 ? '返回科目首页' : '返回 CSCA 模式选择'}
          </Link>
          <h1 className="mt-3 text-3xl font-extrabold text-[#312E81] md:text-4xl">{fixedLevel === 1 ? 'CSCA 试卷与复习资料' : fixedLevel === 2 || fixedLevel === 3 ? 'CSCA 往年试卷' : 'CSCA 试卷'}</h1>
          {!fixedLevel && <p className="mt-2 text-sm text-gray-500">选择科目和考试月份。题目将按原始题号固定顺序加载。</p>}
        </div>
        {!fixedLevel && <Link to="/history" className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 bg-white px-4 py-2.5 text-sm font-bold text-[#4F46E5] hover:bg-indigo-50">
          <History className="h-4 w-4" /> 查看考试记录
        </Link>}
      </div>

      {error && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      {!fixedLevel ? <section className="rounded-3xl border border-indigo-50 bg-white p-5 shadow-sm md:p-6">
        <h2 className="mb-4 text-lg font-bold text-[#312E81]">选择科目</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          {subjects.map(subject => {
            const Icon = subjectIcons[subject.code] || BookOpen;
            const active = selectedLevel === subject.level;
            return (
              <button
                key={subject.level}
                type="button"
                disabled={!subject.available}
                onClick={() => setSelectedLevel(subject.level)}
                className={`relative rounded-2xl border-2 p-4 text-left transition-all ${active ? 'border-[#4F46E5] bg-indigo-50' : 'border-gray-100 bg-white'} disabled:cursor-not-allowed disabled:opacity-55`}
              >
                <Icon className={`h-6 w-6 ${active ? 'text-[#4F46E5]' : 'text-gray-400'}`} />
                <div className={`mt-3 font-bold ${active ? 'text-[#312E81]' : 'text-gray-500'}`}>{subject.name}</div>
                <div className="mt-1 text-xs font-semibold text-slate-500">level {subject.level}</div>

                {!subject.available && <Lock className="absolute right-3 top-3 h-4 w-4 text-gray-300" />}
              </button>
            );
          })}
        </div>
      </section> : null}

      {loading ? (
        <div className="py-16 text-center text-gray-500">正在加载真实题库目录…</div>
      ) : (
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {visiblePapers.map(paper => (
            <article key={paper.id} className="flex flex-col rounded-3xl border border-indigo-50 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
              <div className="flex items-start justify-between gap-3">
                <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${accent.icon}`}>
                  {paper.level === 3 ? <Calculator className="h-6 w-6" /> : <BookOpen className="h-6 w-6" />}
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${paper.available ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                  {paper.available ? '题库已接入' : '题库待接入'}
                </span>
              </div>
              <div className={`mt-4 text-sm font-bold ${accent.text}`}>CSCA {paper.subject}</div>
              <h2 className="mt-1 text-2xl font-extrabold text-[#312E81]">{paper.year}年{paper.month}月</h2>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-gray-500">
                <div className="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2"><FileText className="h-4 w-4" /> {paper.question_count} 题</div>
                <div className="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2"><Clock className="h-4 w-4" /> {Math.round(paper.durationSeconds / 60)} 分钟</div>
              </div>
              {paper.sourceNote && <p className="mt-3 text-sm text-amber-700">{paper.sourceNote}</p>}
              {!!paper.pending_count && <p className="mt-2 text-xs text-amber-700">{paper.pending_count} 题待核对，保留作答但暂不计分。</p>}
              {!fixedLevel && <Link className="mt-3 text-sm font-bold text-indigo-600" to={topicsPath}>按本科目题型练习 →</Link>}
              <div className="mt-4 min-h-10 text-sm text-gray-500">
                {paper.active_test_id ? (
                  <span className="font-semibold text-amber-700">有一场未完成考试，可继续作答</span>
                ) : paper.history_count > 0 ? (
                  <span>最近成绩：<strong className="text-[#312E81]">{paper.latest_percentage}%</strong> · 共 {paper.history_count} 次记录</span>
                ) : (
                  <span>尚未参加这套试卷</span>
                )}
              </div>
              <div className="mt-auto flex items-center gap-2 pt-5">
                <Link
                  to={`/csca/past-papers/${paper.id}`}
                  aria-disabled={!paper.available}
                  className={`flex flex-1 items-center justify-center gap-1 rounded-xl px-4 py-3 font-bold text-white ${paper.available ? accent.button : 'pointer-events-none bg-gray-300'}`}
                >
                  {paper.active_test_id ? '继续考试' : '开始考试'} <ChevronRight className="h-4 w-4" />
                </Link>
                {!fixedLevel && paper.history_count > 0 && (
                  <Link to="/history" className="rounded-xl border border-indigo-200 p-3 text-[#4F46E5] hover:bg-indigo-50" aria-label="查看记录">
                    <History className="h-5 w-5" />
                  </Link>
                )}
              </div>
            </article>
          ))}
        </section>
      )}
    </div>
  );
};

export default CSCAPastPapers;
